import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaWhatsapp, FaPhone, FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';

const ChatbotContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
`;

const ChatbotButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--theme-accent);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px var(--theme-shadow-accent);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: scale(1.1);
    background: var(--theme-accent-strong);
    box-shadow: 0 6px 16px var(--theme-shadow-accent);
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover::before {
    transform: translateX(100%);
  }

  svg {
    width: 30px;
    height: 30px;
    fill: white;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
`;

const ChatbotWindow = styled.div`
  position: fixed;
  bottom: 5rem;
  right: 2rem;
  width: 350px;
  height: 500px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
`;

const ChatbotHeader = styled.div`
  padding: 1rem;
  background: var(--theme-accent);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 1.1rem;
  }

  button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 1.2rem;
  }
`;

const ChatbotMessages = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Message = styled.div`
  max-width: 80%;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.4;

  &.user {
    align-self: flex-end;
    background: var(--theme-accent);
    color: white;
    border-bottom-right-radius: 4px;
  }

  &.bot {
    align-self: flex-start;
    background: #f0f0f0;
    color: #333;
    border-bottom-left-radius: 4px;
  }
`;

const ChatbotInput = styled.div`
  padding: 1rem;
  border-top: 1px solid #eee;
  display: flex;
  gap: 0.5rem;

  input {
    flex: 1;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.9rem;
    outline: none;

    &:focus {
      border-color: var(--theme-accent);
    }
  }

  button {
    padding: 0.8rem 1.2rem;
    background: var(--theme-accent);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: var(--theme-accent-strong);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 0.8rem 0 0.5rem 0;
  border-top: 1px solid #eee;
  background: #fafafa;
`;

const AnimatedIcon = styled.span`
  display: inline-block;
  font-size: 2.2rem;
  line-height: 1;
  animation: bounce 1.2s infinite alternate, glow 2s infinite alternate;
  filter: drop-shadow(0 0 8px var(--theme-accent));

  @keyframes bounce {
    0% { transform: translateY(0); }
    100% { transform: translateY(-10px) scale(1.08); }
  }
  @keyframes glow {
    0% { filter: drop-shadow(0 0 8px var(--theme-accent)); }
    100% { filter: drop-shadow(0 0 18px var(--theme-accent)); }
  }
`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: 'Hello! I\'m your VVV IT Solutions assistant. How can I help you today?'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const qaPairs = {
    'services': 'We offer Web Development, Mobile App Development, Cloud Solutions, and IT Security services. You can find more details in our Services section.',
    'contact': 'You can reach us through our Contact page or email us at info@vvvit.com',
    'career': 'We\'re always looking for talented individuals. Check our Career page for current openings.',
    'about': 'VVV IT Solutions is a leading technology company specializing in innovative software solutions.',
    'web development': 'Our web development services include custom websites, e-commerce solutions, and web applications using modern technologies.',
    'mobile app': 'We develop native and cross-platform mobile applications for iOS and Android platforms.',
    'cloud': 'Our cloud solutions include migration, infrastructure setup, security, and management services.',
    'security': 'We provide comprehensive IT security solutions including network security, data protection, and security audits.',
    'help': 'I can help you with information about our services, contact details, career opportunities, and more. Just ask!'
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Voice greeting on first load
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const greeting = 'Hello! Welcome to VVV IT Solutions. How can I assist you today?';
      const utterance = new window.SpeechSynthesisUtterance(greeting);
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    let response = "I'm not sure I understand. Could you please rephrase your question?";
    
    for (const [key, value] of Object.entries(qaPairs)) {
      if (lowerMessage.includes(key)) {
        response = value;
        break;
      }
    }
    
    return response;
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: inputValue }]);
    
    // Get bot response
    const botResponse = getBotResponse(inputValue);
    
    // Add bot response after a short delay
    setTimeout(() => {
      setMessages(prev => {
        const newMessages = [...prev, {
          type: 'bot',
          content: botResponse
        }];
        // Dispatch custom event for character speech
        window.dispatchEvent(new CustomEvent('chatbot-speech', { detail: { text: botResponse } }));
        return newMessages;
      });
    }, 1000);

    setInputValue('');
  };

  return (
    <ChatbotContainer>
      <ChatbotButton onClick={() => setIsOpen(!isOpen)}>
        <AnimatedIcon role="img" aria-label="character">🧑‍💻</AnimatedIcon>
      </ChatbotButton>

      <ChatbotWindow $isOpen={isOpen}>
        <ChatbotHeader>
          <h3>VVV IT Solutions Assistant</h3>
          <button onClick={() => setIsOpen(false)}>×</button>
        </ChatbotHeader>
        
        <ChatbotMessages>
          {messages.map((message, index) => (
            <Message key={index} className={message.type}>
              {message.content}
            </Message>
          ))}
          <div ref={messagesEndRef} />
        </ChatbotMessages>

        <ChatbotInput>
          <form onSubmit={handleSendMessage} style={{ display: 'flex', width: '100%', gap: '0.5rem' }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask me anything about our services..."
            />
            <button type="submit">Send</button>
          </form>
        </ChatbotInput>
        <SocialLinks>
          <a href="https://wa.me/919336652601" target="_blank" rel="noopener noreferrer" title="WhatsApp"><FaWhatsapp size={22} color="#25D366" /></a>
          <a href="tel:+919336652601" title="Call"><FaPhone size={22} color="var(--theme-accent)" /></a>
          <a href="https://facebook.com/" target="_blank" rel="noopener noreferrer" title="Facebook"><FaFacebook size={22} color="#4267B2" /></a>
          <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" title="Twitter"><FaTwitter size={22} color="#1DA1F2" /></a>
          <a href="https://linkedin.com/" target="_blank" rel="noopener noreferrer" title="LinkedIn"><FaLinkedin size={22} color="#0077B5" /></a>
        </SocialLinks>
      </ChatbotWindow>
    </ChatbotContainer>
  );
};

export default Chatbot; 
